import React, { RefObject, useState } from 'react';
import s from './CompareTable.scss';
import classNames from 'classnames';
import { InfoIcon, Collapse, TextButton } from '@wix/design-system';
import Confirm from 'wix-ui-icons-common/Confirm';
import Minus from 'wix-ui-icons-common/Minus';
import { formatNumber } from '../../constants';

import EmailMarketing from '../../assets/icons/CompareTable/EmailMarketing.svg?url';
import Forms from '../../assets/icons/CompareTable/Forms.svg?url';
import Automations from '../../assets/icons/CompareTable/Automations.svg?url';
import SocialPost from '../../assets/icons/CompareTable/SocialPost.svg?url';
import VideoMaker from '../../assets/icons/CompareTable/VideoMaker.svg?url';
import PriceQuotes from '../../assets/icons/CompareTable/PriceQuotes.svg?url';
import Chat from '../../assets/icons/CompareTable/Chat.svg?url';

import ChevronDown from 'wix-ui-icons-common/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/ChevronUp';
import ChevronDownSmall from 'wix-ui-icons-common/ChevronDownSmall';
import ChevronUpSmall from 'wix-ui-icons-common/ChevronUpSmall';
import { useTranslation } from 'react-i18next';
import { data, biLogger } from '../../globals';
import { scrollToRef } from '../../services/window-utils';

const DisplayByProduct = (props: {
  product: { type: any; value: any; amount?: any };
  datahook: string;
  isHighlighted?: boolean;
}) => {
  const { product } = props;
  const { t } = useTranslation();

  const displayByType = () => {
    const { decimalSeparator, groupSeparator } = data.currency;
    if (product.type === 'boolean') {
      return product.value ? (
        <div data-hook="featureTrue">
          <Confirm className={s.confirm} />
        </div>
      ) : (
        <div data-hook="featureFalse">
          <Minus className={s.minus} />
        </div>
      );
    } else if (product.type === 'number') {
      return formatNumber(product.value, decimalSeparator, groupSeparator);
    } else {
      return t(product.value, {
        amount: formatNumber(product.amount, decimalSeparator, groupSeparator),
      });
    }
  };

  return (
    <div
      className={classNames(s.cell, {
        [s.mostPopularCell]: props.isHighlighted,
      })}
      data-hook={props.datahook}
    >
      {displayByType()}
    </div>
  );
};

const SingleFeature = (props: {
  feature: any;
  isUnlimitedHighlighted: boolean;
}) => {
  const { feature, isUnlimitedHighlighted } = props;
  const { t } = useTranslation();

  return (
    <div className={s.grid}>
      <div className={classNames(s.cell, s.featureTitle)}>
        <div className={s.featureName} data-hook="featureTitle">
          {t(feature.title)}
        </div>
        <InfoIcon
          size="small"
          content={t(feature.info)}
          tooltipProps={{ maxWidth: 294, placement: 'right' }}
          dataHook="featureInfoIcon"
          className={s.info}
        />
      </div>
      <DisplayByProduct
        product={feature.products.unlimited}
        isHighlighted={isUnlimitedHighlighted}
        datahook="featureByProduct-unlimited"
      />
      <DisplayByProduct
        product={feature.products.pro}
        isHighlighted={!isUnlimitedHighlighted}
        datahook="featureByProduct-pro"
      />
      <DisplayByProduct
        product={feature.products.basic}
        datahook="featureByProduct-basic"
      />
      <DisplayByProduct
        product={feature.products.free}
        datahook="featureByProduct-free"
      />
    </div>
  );
};

const SingleTool = (props: {
  tool: any;
  isUnlimitedHighlighted: boolean;
  isShowAll: boolean;
}) => {
  const { tool, isUnlimitedHighlighted, isShowAll } = props;
  const { t } = useTranslation();
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const featuresList: any[] = Object.entries(tool.features).map(
    ([_key, value]) => value,
  );
  featuresList.sort((a, b) => (a.order > b.order ? 1 : -1));

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'EmailMarketing':
        return EmailMarketing;
      case 'Forms':
        return Forms;
      case 'Automations':
        return Automations;
      case 'SocialPost':
        return SocialPost;
      case 'VideoMaker':
        return VideoMaker;
      case 'PriceQuotes':
        return PriceQuotes;
      case 'Chat':
        return Chat;
      default:
        return '';
    }
  };

  const onShowMoreLessClicked = (ascend_product: string) => {
    const action = isCollapseOpen ? 'less' : 'more';
    setIsCollapseOpen(!isCollapseOpen);
    biLogger.showMoreLess({
      action,
      productName: 'lower_table',
      ascend_product,
    });
  };

  return (
    <div>
      <div className={classNames(s.grid, { [s.firstTool]: tool.order === 0 })}>
        <div className={classNames(s.toolHeader)}>
          {tool.icon && (
            <img
              src={getIcon(tool.icon)}
              className={s.icon}
              alt={tool.icon}
              data-hook="toolIcon"
            />
          )}
          <div className={s.toolName} data-hook="toolTitle">
            {t(tool.title)}
          </div>
        </div>
      </div>
      {featuresList.slice(0, isShowAll ? 9999 : 3).map((feature, id) => (
        <SingleFeature
          feature={feature}
          key={id}
          isUnlimitedHighlighted={isUnlimitedHighlighted}
        />
      ))}
      {!isShowAll && featuresList.length > 3 && (
        <div>
          <Collapse open={isCollapseOpen}>
            {featuresList.slice(3).map((feature, id) => (
              <SingleFeature
                feature={feature}
                key={id}
                isUnlimitedHighlighted={isUnlimitedHighlighted}
              />
            ))}
          </Collapse>
          <div className={s.grid}>
            <div className={classNames(s.showMore)}>
              <TextButton
                skin="dark"
                underline="always"
                suffixIcon={
                  isCollapseOpen ? <ChevronUpSmall /> : <ChevronDownSmall />
                }
                onClick={() => onShowMoreLessClicked(tool.biName)}
                dataHook="showMore"
                size="small"
              >
                {isCollapseOpen
                  ? t('compare.toolsList.showLess')
                  : t('compare.toolsList.showMore', {
                      toolsNum: featuresList.length - 3,
                    })}
              </TextButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ToolsListSingleProduct = (props: {
  content: any[];
  isUnlimitedHighlighted: boolean;
  isHeaderSticky: boolean;
}) => {
  const { content, isUnlimitedHighlighted, isHeaderSticky } = props;
  const [isShowAll, setisShowAll] = useState(false);
  const { t } = useTranslation();

  const scrollRef: RefObject<HTMLDivElement> = React.createRef();

  const arrayOfForms = [];
  arrayOfForms.push(content[0]);
  const arrayOfRest = content.slice(1);

  const onShowAllClicked = () => {
    const action = isShowAll ? 'less' : 'more';
    setisShowAll(!isShowAll);
    biLogger.showMoreLess({
      action,
      productName: 'lower_table',
      ascend_product: content[0],
    });
    if (isShowAll) {
      scrollToRef(scrollRef);
    }
  };

  return (
    <div
      className={classNames({ [s.compensateStickyHeader]: isHeaderSticky })}
      data-hook="toolsList"
    >
      <div className={s.scrollPosition} ref={scrollRef} />
      {arrayOfForms.map((tool, id) => (
        <SingleTool
          tool={tool}
          key={id}
          isUnlimitedHighlighted={isUnlimitedHighlighted}
          isShowAll={true}
        />
      ))}
      <div className={s.bottomMessage} data-hook="bottomMessage">
        {t('headerForms.footer')}
      </div>
      {isShowAll && (
        <div className={s.restOfFeatureTable}>
          {arrayOfRest.map((tool, id) => (
            <SingleTool
              tool={tool}
              key={id}
              isUnlimitedHighlighted={isUnlimitedHighlighted}
              isShowAll={false}
            />
          ))}
        </div>
      )}
      <div className={s.showAll}>
        <div className={s.breakLine} />
        <div
          className={s.showAllButton}
          onClick={onShowAllClicked}
          data-hook="showAll"
        >
          <span className={s.showAllText}>
            {isShowAll ? t('headerForms.showLess') : t('headerForms.showMore')}
          </span>
          {isShowAll ? <ChevronUp size="24px" /> : <ChevronDown size="24px" />}
        </div>
      </div>
    </div>
  );
};

export default ToolsListSingleProduct;
